import Q from 'q';
import qlimit from 'qlimit';
import picsioUtils from '@picsio/utils';
import sdk from '../sdk';

const sendFile = (url, file, storageId, revisionId, partNum, onProgress) => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  xhr.upload.onprogress = ({ loaded, total }) => onProgress(loaded, total, xhr);
  xhr.onload = () => {
    const etag = xhr.getResponseHeader('etag');
    if (!etag) return reject('Error getting ETag from response');

    return resolve({
      storageId,
      etag,
      partNum,
      headRevisionId: revisionId,
      fileSize: file.size.toString(),
    });
  };
  xhr.onabort = reject;
  xhr.onerror = reject;
  xhr.open('PUT', url, true);
  xhr.setRequestHeader('content-disposition', 'attachment');
  xhr.send(file);
});

const repeat = async (fn, maxTries = 3, tryNumber = 1) => {
  try {
    return await fn();
  } catch (err) {
    const delay = 50 * (2 ** tryNumber);
    if (err.type === 'abort') throw err;
    if (tryNumber >= maxTries) {
      throw err;
    } else {
      console.log(`${err.message || 'Something went wrong'}, trying again in ${delay / 1000} sec...`);
      return Q.Promise((resolve) => setTimeout(() => resolve(repeat(fn, maxTries, tryNumber + 1)), delay));
    }
  }
};

export default {
  /**
   * Upload file to the S3
   * @param {Object} params
   * @param {*} prams.file - file from filesystem
   * @param {string?} prams.collectionId
   * @param {string?} params.lightboardId
   * @param {Function?} params.onUploadProgress
   * @param {Function?} params.id - for updlaod revision
   * @returns {Promise}
   */
  async uploadFile({
    file, collectionId, lightboardId, onUploadProgress = () => {}, id,
  }) {
    /** Normalize decomposed symbols into composed */
    const fileName = file.name.normalize('NFC');
    const fileSize = file.size;
    let mimeType = file.type;
    const normalizedMimeType = typeof mimeType === 'string' ? mimeType.trim().toLowerCase() : '';
    if (!normalizedMimeType || normalizedMimeType === 'application/octet-stream') {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      mimeType = picsioUtils.lookupMimeType(fileExtension) || 'application/octet-stream';
    }
    const { data: res } = await sdk.assets.buildS3UploadLink({
      fileName,
      fileSize,
      mimeType,
      collectionId,
      lightboardId,
      assetId: id,
    });

    const {
      urls: urlsData, uploadId, storageId, revisionId, chunkNums,
    } = res;

    const chunkSize = file.size / chunkNums;

    const fileBlobChunks = [...Array(chunkNums).keys()].map((num) => {
      const start = num * chunkSize;
      const end = (num + 1) * chunkSize;
      return num < chunkNums
        ? file.slice(start, end, mimeType)
        : file.slice(start, undefined, mimeType);
    });

    const chunkUploadLimit = qlimit(5);
    const initialPercentage = 0;
    let uploadAborted = false;
    const uploadProgress = {};

    const resAll = await Q.all(
      urlsData.map(
        chunkUploadLimit(async (urlObject, index) => {
          try {
            if (uploadAborted) throw new Error('Aborted');
            const chunk = fileBlobChunks[urlObject.partNum - 1];

            const uploadFunc = sendFile.bind(this, urlObject.url, chunk, storageId, revisionId, urlObject.partNum, (loaded, total, xhr) => {
              uploadProgress[index] = initialPercentage + (loaded / chunkNums / total) * 100;
              onUploadProgress({
                percentage: uploadProgress ? Object.values(uploadProgress).reduce((sum, num) => sum + num, 0) : 0,
                xhr,
              });
            });
            return await repeat(uploadFunc);
          } catch (err) {
            uploadAborted = true;
            uploadProgress[index] = 0;
            throw err;
          }
        }),
      ),
    );
    const parts = resAll.map((item) => ({
      ETag: item.etag,
      PartNumber: item.partNum,
    }));

    await sdk.assets.completeMultipart({ parts, uploadId, storageId });
    return { storageId, headRevisionId: revisionId, fileSize: fileSize.toString() };
  },
};
